import { Link, Panel, PanelType } from '@fluentui/react';
import {
  Button,
  Dropdown,
  Field,
  FluentProvider,
  Option,
  Spinner,
  Text,
  Textarea,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMetadataEntities } from '../../services/metadataService';

interface IStatusOption {
  key: string;
  text: string;
}

interface UpdateMetadataStatusPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  statusSelection: string;
  onStatusChange: (value: string) => void;
  statusComment: string;
  onCommentChange: (value: string) => void;
  allowedOptions: IStatusOption[];
  allowedText: string;
  targetDisplayName?: string;
  updateDisabled?: boolean;
  isUpdating?: boolean;
  validationErrors?: Record<string, string> | null;
  entityType?: string;
  metadataName?: string;
  onCancel: () => void;
  onUpdate: () => void;
}

const entityEditPath = (entityType: string, id: number) => {
  switch (entityType) {
    case 'Work':
      return `/editWork?id=${id}`;
    case 'Asset':
      return `/editAsset?id=${id}`;
    case 'Order':
      return `/editOrder?id=${id}`;
    default:
      return undefined;
  }
};

const UpdateMetadataStatusPanel = ({
  isOpen,
  onDismiss,
  statusSelection,
  onStatusChange,
  statusComment,
  onCommentChange,
  allowedOptions,
  allowedText,
  targetDisplayName,
  updateDisabled,
  isUpdating,
  validationErrors,
  entityType,
  metadataName,
  onCancel,
  onUpdate,
}: UpdateMetadataStatusPanelProps) => {
  const navigate = useNavigate();
  const errorEntries = validationErrors ? Object.entries(validationErrors) : [];
  const [fieldEntityIds, setFieldEntityIds] = useState<
    Record<string, number[]>
  >({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string>();
  const detailsLoaded = Object.keys(fieldEntityIds).length > 0;
  const canLoadDetails =
    errorEntries.length > 0 && Boolean(entityType && metadataName);

  useEffect(() => {
    setFieldEntityIds({});
    setDetailsError(undefined);
    setIsLoadingDetails(false);
  }, [isOpen, validationErrors]);

  const loadDetails = async () => {
    if (!entityType || !metadataName || errorEntries.length === 0) return;
    setIsLoadingDetails(true);
    setDetailsError(undefined);
    const abortController = new AbortController();
    try {
      const fields = errorEntries.map(([field]) => field);
      const response = await getMetadataEntities(
        abortController,
        entityType,
        metadataName,
        fields
      );
      const byField: Record<string, number[]> = {};
      for (const row of response.fieldEntityIds ?? []) {
        byField[row.field] = row.entities ?? [];
      }
      setFieldEntityIds(byField);
    } catch (error: any) {
      setDetailsError(error?.message ?? 'Failed to load records.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close"
      headerText="Update metadata status"
    >
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {targetDisplayName ? (
            <Text size={200}>Target: {targetDisplayName}</Text>
          ) : null}
          <Field label="Status">
            <Dropdown
              value={statusSelection}
              selectedOptions={[statusSelection]}
              onOptionSelect={(_ev, data) =>
                onStatusChange(data.optionValue ?? 'Draft')
              }
            >
              {allowedOptions.map((option) => (
                <Option key={option.key} value={option.key}>
                  {option.text}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="Comment">
            <Textarea
              value={statusComment}
              onChange={(_ev, data) => onCommentChange(data.value)}
              resize="vertical"
            />
          </Field>
          <Text size={200} style={{ color: '#605e5c' }}>
            {allowedText}
          </Text>
          {errorEntries.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: 12,
                borderRadius: tokens.borderRadiusMedium,
                background: tokens.colorPaletteRedBackground1,
                border: `1px solid ${tokens.colorPaletteRedBorder1}`,
              }}
            >
              <Text
                size={200}
                weight="semibold"
                style={{ color: tokens.colorPaletteRedForeground1 }}
              >
                Validation failed. Fix the following before approving:
              </Text>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errorEntries.map(([field, message]) => {
                  const entities = fieldEntityIds[field] ?? [];
                  return (
                    <li
                      key={field}
                      style={{
                        color: tokens.colorPaletteRedForeground1,
                        fontSize: 12,
                        marginBottom: 6,
                      }}
                    >
                      <strong>{field}</strong>: {message}
                      {detailsLoaded && (
                        <div style={{ marginTop: 4 }}>
                          {entities.length > 0 ? (
                            <>
                              In use by ({entities.length}):{' '}
                              {entities.map((id, index) => {
                                const path =
                                  entityType && entityEditPath(entityType, id);
                                return (
                                  <span key={id}>
                                    {index > 0 ? ', ' : null}
                                    {path ? (
                                      <Link
                                        onClick={() => navigate(path)}
                                        styles={{
                                          root: {
                                            color:
                                              tokens.colorPaletteRedForeground1,
                                          },
                                        }}
                                      >
                                        {id}
                                      </Link>
                                    ) : (
                                      id
                                    )}
                                  </span>
                                );
                              })}
                            </>
                          ) : (
                            'No related records.'
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {canLoadDetails && !detailsLoaded && (
                <div style={{ marginTop: 4 }}>
                  {isLoadingDetails ? (
                    <Spinner size="tiny" label="Loading records…" />
                  ) : (
                    <Link
                      onClick={() => {
                        void loadDetails();
                      }}
                    >
                      Show details
                    </Link>
                  )}
                </div>
              )}
              {detailsError ? (
                <Text
                  size={200}
                  style={{ color: tokens.colorPaletteRedForeground1 }}
                >
                  {detailsError}
                </Text>
              ) : null}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              appearance="secondary"
              onClick={onCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onUpdate}
              disabled={updateDisabled || isUpdating}
              icon={isUpdating ? <Spinner size="tiny" /> : undefined}
            >
              {isUpdating ? 'Updating…' : 'Update'}
            </Button>
          </div>
        </div>
      </FluentProvider>
    </Panel>
  );
};

export default UpdateMetadataStatusPanel;
